import React, { useState, useEffect, useRef } from 'react';
import useApi from '../../hooks/useApi';
import { Application, Permission } from '../../types';
import styles from './EditAppModal.module.css';

interface EditAppModalProps {
  app: Application | null;
  permissions: Permission[];
  onClose: () => void;
  onAppUpdated: (updatedApp: Application) => void;
  onAppRemoved: (appId: string | number) => Promise<boolean>;
}

const EditAppModal: React.FC<EditAppModalProps> = ({ app, permissions, onClose, onAppUpdated, onAppRemoved }) => {
  const [name, setName] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [newLogoFile, setNewLogoFile] = useState<File | null>(null);
  const [permission, setPermission] = useState<string>('');
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false);
  const { securedRequest, loading } = useApi();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (app) {
      setName(app.name);
      setUrl(app.url);
      setPermission(app.permission || '');
      setNewLogoFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [app]);

  if (!app) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name || !url || !permission) {
      alert('Please fill all fields.');
      return;
    }

    // Step 1: Update the application's text data
    const appData = { name, url, permission };
    const { data: updatedApp, error: updateError } = await securedRequest(
      'put',
      `/api/applications/${app.id}`,
      appData
    );

    if (updateError) {
      alert('Failed to update application.');
      console.error(updateError);
      return;
    }

    let finalApp = updatedApp;

    // Step 2: If a new logo was selected, upload it
    if (newLogoFile) {
      const logoFormData = new FormData();
      logoFormData.append('logoFile', newLogoFile);
      const { data: appWithNewLogo, error: uploadError } = await securedRequest(
        'put',
        `/api/applications/${app.id}/logo`,
        logoFormData
      );

      if (uploadError) {
        alert('Application details updated, but failed to upload new logo.');
        console.error(uploadError);
      } else {
        finalApp = appWithNewLogo;
      }
    }

    onAppUpdated(finalApp);
    onClose();
  };

  const handleDelete = async () => {
    const success = await onAppRemoved(app.id);
    if (success) {
      onClose();
    }
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modalContent} card`}>
        <div className="card-body">
          {isConfirmingDelete ? (
            <div>
              <h5 className="card-title">Confirm Deletion</h5>
              <p>Are you sure you want to delete "{app.name}"?</p>
              <p>This will remove the application for all users and cannot be undone.</p>
              <div className="d-flex justify-content-end">
                <button type="button" className="btn btn-secondary me-2" onClick={() => setIsConfirmingDelete(false)} disabled={loading}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={loading}>
                  {loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <h5 className="card-title">Edit Application</h5>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="appName" className="form-label">App Name</label>
                  <input id="appName" type="text" className="form-control" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} required />
                </div>
                <div className="mb-3">
                  <label htmlFor="appUrl" className="form-label">App URL</label>
                  <input id="appUrl" type="url" className="form-control" value={url} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)} required />
                </div>
                <div className="mb-3">
                  <label htmlFor="appLogo" className="form-label">Upload New Logo (Optional)</label>
                  <input type="file" className="form-control" id="appLogo" ref={fileInputRef} onChange={(e) => setNewLogoFile(e.target.files ? e.target.files[0] : null)} />
                  {app.logoUrl && (
                    <div className="mt-2">
                      <small>Current Logo</small><br/>
                      <img src={app.logoUrl} alt="Current Logo" style={{ maxWidth: '300px', maxHeight: '300px', marginTop: '5px' }} />
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label htmlFor="appPermission" className="form-label">Permission</label>
                  <select id="appPermission" className="form-select" value={permission} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPermission(e.target.value)} required>
                    <option value="" disabled>Select a permission</option>
                    {permissions.map(p => (
                      <option key={p.value} value={p.value}>{p.value} - {p.description}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex justify-content-end">
                  <button type="button" className="btn btn-danger me-auto" onClick={() => setIsConfirmingDelete(true)}>Delete</button>
                  <button type="button" className="btn btn-secondary me-2" onClick={onClose} disabled={loading}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditAppModal;
